import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress,
  styled,
  Chip
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import NotificationService, { Notification } from '../../services/NotificationService';
import { useAuth } from '../../contexts/AuthContext';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#0D47A1',
    color: 'white',
  },
}));

const NotificationContainer = styled(Box)(({ theme }) => ({
  width: 400,
  maxHeight: 500,
  overflow: 'auto',
  padding: 0,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#bbb',
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#888',
  },
}));

const NotificationHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #e0e0e0',
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

const NotificationItem = styled(ListItem)(({ theme }) => ({
  borderBottom: '1px solid #f0f0f0',
  padding: theme.spacing(1.5, 2),
  '&:hover': {
    backgroundColor: '#f9f9f9',
  },
}));

const EmptyNotification = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

// Bildirim durumuna göre renk belirleme
const getStatusColor = (status: string) => {
  switch (status) {
    case 'sent':
      return 'success.light'; // Yeşil
    case 'pending':
      return 'warning.light'; // Turuncu
    default:
      return 'success.light'; // Varsayılan yeşil
  }
};

// Tarih formatı
const formatDate = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleString('tr-TR', { 
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const NotificationMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // 🔧 FIX: viewedNotifications'ı useRef ile tutarak döngüyü önle
  const viewedNotificationsRef = useRef<Set<string>>(
    (() => {
      const saved = localStorage.getItem('viewedNotifications');
      return saved ? new Set<string>(JSON.parse(saved)) : new Set<string>();
    })()
  );
  
  // State olarak da tut (UI güncellemesi için)
  const [viewedNotifications, setViewedNotifications] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('viewedNotifications');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  
  const { currentUser } = useAuth();
  
  // İlk yükleme flag'i
  const isInitialLoadRef = useRef(true);
  const companyIdRef = useRef<string | null>(null);
  
  // Okundu bildirimleri localStorage'a kaydet
  useEffect(() => {
    // Ref'i de güncelle
    viewedNotificationsRef.current = viewedNotifications;
    localStorage.setItem('viewedNotifications', JSON.stringify(Array.from(viewedNotifications)));
  }, [viewedNotifications]);
  
  // Bildirime tıklandığında admin panelinde "görüntülendi" olarak işaretle (localStorage)
  // NOT: Bu ADMIN paneline özel rozet semantiğidir; .NET API'nin kullanıcı okuma
  // durumu (mark-read) mobil/uçtaki kullanıcı içindir, burada yazılmaz.
  const handleNotificationClick = (notificationId: string) => {
    if (!currentUser) return;
    
    setViewedNotifications(prev => {
      const newSet = new Set(prev);
      newSet.add(notificationId);
      return newSet;
    });
  };
  
  // Menüyü açma/kapama
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
    
    // Menu açıldığında tüm bildirimleri işaretle
    if (notifications.length > 0) {
      notifications.forEach(notification => {
        if (!viewedNotifications.has(notification.id)) {
          handleNotificationClick(notification.id);
        }
      });
    }
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Bildirimleri yükle - 🔧 FIX: viewedNotifications dependency kaldırıldı
  const loadNotifications = useCallback(async () => {
    if (!currentUser || !companyIdRef.current) return;
    
    setLoading(true);
    try {
      const recentNotifications = await NotificationService.getRecentNotifications(companyIdRef.current, 20);
      setNotifications(recentNotifications);
      
      // Ref'ten mevcut değeri al (dependency döngüsü olmadan)
      const viewedSet = new Set(viewedNotificationsRef.current);
      
      // Admin'in henüz açmadığı (görüntülemediği) gönderilmiş bildirimleri say
      const newCount = recentNotifications.filter(n => {
        return n.status === 'sent' && !viewedSet.has(n.id);
      }).length;
      
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Bildirimler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]); // 🔧 FIX: Sadece currentUser dependency
  
  // Bildirim servisi başlatma - 🔧 FIX: Sadece currentUser değiştiğinde çalışır
  useEffect(() => {
    let isMounted = true;
    
    const setupNotifications = async () => {
      if (!currentUser) return;
      
      // Zaten yüklendiyse tekrar yükleme (döngü önleme)
      if (!isInitialLoadRef.current && companyIdRef.current) {
        return;
      }
      
      try {
        // Kullanıcının şirket ID'sini al
        const userCompanyId = await NotificationService.getCurrentUserCompany(currentUser.uid);
        if (!userCompanyId || !isMounted) return;
        
        // Ref'e kaydet (diğer fonksiyonlar için)
        companyIdRef.current = userCompanyId;
        setCompanyId(userCompanyId);
        
        if (!isMounted) return;
        
        // Bildirimleri yükle (.NET notification API üzerinden)
        await loadNotifications();
        
        // İlk yükleme tamamlandı
        isInitialLoadRef.current = false;
        
      } catch (error) {
        console.error('Bildirim servisi başlatma hatası:', error);
      }
    };
    
    setupNotifications();
    
    // Temizleme işlevi
    return () => {
      isMounted = false;
    };
  }, [currentUser, loadNotifications]);
  
  return (
    <>
      <IconButton 
        color="inherit" 
        onClick={handleMenuOpen}
        sx={{ marginRight: 2 }}
      >
        <StyledBadge badgeContent={unreadCount > 0 ? unreadCount : null} max={99}>
          <NotificationsIcon />
        </StyledBadge>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { width: 400, maxHeight: 600, overflow: 'hidden' }
        }}
      >
        <NotificationHeader>
          <Typography variant="subtitle1" fontWeight="bold">
            Bildirimler
          </Typography>
          {unreadCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {unreadCount} yeni bildirim
            </Typography>
          )}
        </NotificationHeader>
        
        <NotificationContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : notifications.length > 0 ? (
            <List disablePadding>
              {notifications.map((notification) => {
                // Admin panelinde bu bildirim görüntülendi mi? (localStorage rozet semantiği)
                const isViewed = viewedNotifications.has(notification.id);
                
                // Durum metni
                const statusText = notification.status === 'sent' 
                  ? 'Gönderildi'
                  : notification.status === 'pending' 
                    ? 'Beklemede' 
                    : 'Gönderildi';
                
                return (
                  <React.Fragment key={notification.id}>
                    <NotificationItem
                      onClick={() => handleNotificationClick(notification.id)}
                      sx={{ 
                        borderLeft: '4px solid',
                        borderLeftColor: getStatusColor(notification.status),
                        backgroundColor: isViewed ? 'transparent' : 'rgba(25, 118, 210, 0.05)',
                        cursor: 'pointer'
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: isViewed ? getStatusColor(notification.status) : 'primary.main',
                          color: 'white'
                        }}>
                          {isViewed ? <MarkEmailReadIcon /> : <MarkEmailUnreadIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {notification.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                              <Chip 
                                label={statusText} 
                                size="small" 
                                color={
                                  notification.status === 'sent' 
                                    ? 'success'
                                    : notification.status === 'pending' 
                                      ? 'warning' 
                                      : 'success'
                                }
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(notification.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                              {notification.body}
                            </Typography>
                            
                            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {/* NOT: .NET /list endpoint'i alıcı/okuyan UID listesi DÖNMEZ,
                                  yalnızca toplam alıcı sayısı (RecipientCount) verir. */}
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                <strong>Alıcı:</strong> {typeof notification.recipientCount === 'number' ? `${notification.recipientCount} alıcı` : 'Bilinmiyor'}
                              </Typography>
                              
                              {notification.sentAt && (
                                <Typography variant="caption" color="text.secondary">
                                  İşlenme: {formatDate(notification.sentAt)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </NotificationItem>
                    <Divider component="li" />
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <EmptyNotification>
              <Typography>Bildirim bulunmuyor</Typography>
            </EmptyNotification>
          )}
        </NotificationContainer>
      </Menu>
    </>
  );
};

export default NotificationMenu; 